/**
 * Frontend Logger Utility
 * Replaces console.log with environment-aware logging
 * Prevents information leakage in production
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Sanitize sensitive data from log objects
 */
function sanitizeData(data) {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const sensitiveKeys = ['password', 'token', 'secret', 'authorization', 'auth', 'jwt', 'api_key', 'apikey', 'csrf'];
  const sanitized = { ...data };

  for (const key in sanitized) {
    const lowerKey = key.toLowerCase();
    if (sensitiveKeys.some(sk => lowerKey.includes(sk))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      sanitized[key] = sanitizeData(sanitized[key]);
    }
  }

  return sanitized;
}

/**
 * Logger class for frontend
 */
class Logger {
  /**
   * Log debug messages (only in development)
   */
  debug(...args) {
    if (isDevelopment) {
      console.debug('[DEBUG]', ...args.map(arg => typeof arg === 'object' ? sanitizeData(arg) : arg));
    }
  }

  /**
   * Log info messages (only in development)
   */
  info(...args) {
    if (isDevelopment) {
      console.info('[INFO]', ...args.map(arg => typeof arg === 'object' ? sanitizeData(arg) : arg));
    }
  }

  /**
   * Log warnings (always logged)
   */
  warn(...args) {
    console.warn('[WARN]', ...args.map(arg => typeof arg === 'object' ? sanitizeData(arg) : arg));
  }

  /**
   * Log errors (always logged)
   */
  error(...args) {
    console.error('[ERROR]', ...args.map(arg => typeof arg === 'object' ? sanitizeData(arg) : arg));
    
    // Optionally send to error tracking service
    if (window.Sentry) {
      try {
        window.Sentry.captureException(args[0] instanceof Error ? args[0] : new Error(String(args[0])));
      } catch (e) {
        // Silently fail if Sentry is not properly initialized
      }
    }
  }

  /**
   * Log with custom level
   */
  log(level, ...args) {
    const sanitized = args.map(arg => typeof arg === 'object' ? sanitizeData(arg) : arg);
    
    switch (level) {
      case 'debug':
        this.debug(...sanitized);
        break;
      case 'info':
        this.info(...sanitized);
        break;
      case 'warn':
        this.warn(...sanitized);
        break;
      case 'error':
        this.error(...sanitized);
        break;
      default:
        if (isDevelopment) {
          console.log(`[${level.toUpperCase()}]`, ...sanitized);
        }
    }
  }

  /**
   * Create a child logger with context
   */
  child(context = {}) {
    const childLogger = new Logger();
    childLogger._context = { ...this._context, ...context };
    
    // Override methods to include context
    const originalMethods = ['debug', 'info', 'warn', 'error'];
    originalMethods.forEach(method => {
      const original = childLogger[method].bind(childLogger);
      childLogger[method] = (...args) => {
        original({ ...childLogger._context, ...args[0] }, ...args.slice(1));
      };
    });
    
    return childLogger;
  }
}

// Create singleton instance
const logger = new Logger();

// Export singleton and class
export default logger;
export { Logger, sanitizeData };
